import { uniqueId } from "lodash";
import * as React from "react";
import { ReactElement } from "react";
import { CssProps, Spacing } from "~/sharedComponents/layout/cssProps";
import { TipIcon } from "~/sharedComponents/tooltip";
import { Flex } from "../layout/Flex";
import { LabelText } from "./fieldsUI/LabelText";
import { RadioValueType } from "./RadioElement";

interface RadioGroupProps {
    selectedValue?: RadioValueType;
    onSelect: (value: RadioValueType) => void;
    label?: string;
    labelColor?: string;
    description?: string;
    disabled?: boolean;
    flexDirection?: CssProps["flexDirection"];
    gap?: Spacing;
}
interface RadioGroupContext {
    name?: string;
    selectedValue?: RadioValueType;
    onSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
}
export const RadioGroupContext = React.createContext<RadioGroupContext>(null);
export class RadioGroup extends React.Component<RadioGroupProps> {
    groupName: string;
    originValType: string;
    constructor(props: RadioGroupProps) {
        super(props);
        this.originValType = this.getChildValueType(this.props.children as ReactElement<any>[]);
        this.groupName = uniqueId("radioGr_");
    }

    getChildValueType = (children: ReactElement<any>[]) => {
        // eslint-disable-next-line eqeqeq
        return children && children[0] && children[0].props && children[0].props.value != undefined
            ? typeof children[0].props.value
            : null;
    };

    convertToOriginValType = (input: any) => {
        if (!this.originValType || this.originValType === "string") {
            return input;
        }

        const targetValType = typeof input;
        if (this.originValType === targetValType) {
            return input;
        }
        switch (this.originValType) {
            case "string":
                return String.bind(null, input)();
            case "number":
                return Number.bind(null, input)();
            case "boolean":
                return input === "true" ? true : false;
            default:
                return input;
        }
    };

    onSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = this.convertToOriginValType(e.target.value);
        this.props.onSelect(value);
    };

    render() {
        const {
            children,
            selectedValue,
            disabled,
            label,
            description,
            labelColor,
            flexDirection = "column",
            gap = 4,
        } = this.props;

        return (
            <RadioGroupContext.Provider
                value={{ name: this.groupName, selectedValue, onSelect: this.onSelect, disabled }}
            >
                {label && (
                    <LabelText small={false} weight="semibold" color={labelColor}>
                        {description ? (
                            <Flex alignItems="center">
                                {label}
                                <TipIcon>{description}</TipIcon>
                            </Flex>
                        ) : (
                            label
                        )}
                    </LabelText>
                )}
                <Flex role="radiogroup" flexDirection={flexDirection} gap={gap}>
                    {children}
                </Flex>
            </RadioGroupContext.Provider>
        );
    }
}
